/**
 * Custom Select Component
 * A fully accessible select component with keyboard navigation and ARIA support
 */

import '../css/custom-select.css';

class CustomSelect {
    // Static registry to track all instances
    static instances = new Map();

    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            placeholder: 'Select an option...',
            searchable: false,
            disabled: false,
            onChange: null,
            trueDefaultValue: null, // The actual default value for change detection
            ...options
        };

        this.isOpen = false;
        this.selectedIndex = -1;
        this.highlightedIndex = -1;
        this.selectOptions = [];
        
        // Track initial value for change detection
        this.initialValue = null;
        this.initialIndex = -1;
        
        this.init();
        
        // Register this instance (only if element has an ID)
        if (element.id) {
            CustomSelect.instances.set(element.id, this);
        }
    }

    init() {
        this.parseOriginalSelect();
        this.createCustomStructure();
        this.bindEvents();
        this.setInitialValue();
    }

    parseOriginalSelect() {
        // Check if the select element itself is disabled
        if (this.element.disabled || this.element.hasAttribute('disabled')) {
            this.options.disabled = true;
        }

        // Get options from the original select element
        const originalOptions = this.element.querySelectorAll('option');
        this.selectOptions = Array.from(originalOptions).map((option, index) => ({
            value: option.value,
            text: option.textContent.trim(),
            selected: option.selected,
            disabled: option.disabled,
            index: index
        }));

        // Find initially selected option
        this.selectedIndex = this.selectOptions.findIndex(opt => opt.selected);
        if (this.selectedIndex === -1 && this.selectOptions.length > 0) {
            this.selectedIndex = 0;
        }
        
        // Store initial value and index
        if (this.initialValue === null) {
            this.initialValue = this.selectOptions[this.selectedIndex]?.value || null;
            this.initialIndex = this.selectedIndex;
        }
    }

    createCustomStructure() {
        // Hide the original select
        this.element.style.display = 'none';
        
        // Mark as initialized
        this.element.setAttribute('data-custom-select-initialized', 'true');
        
        // Create custom select wrapper
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'custom-select';
        this.wrapper.setAttribute('data-open', 'false');

        // Create toggle button
        this.createToggle();

        // Create dropdown
        this.createDropdown();

        // Apply disabled state to wrapper and toggle
        if (this.options.disabled) {
            this.wrapper.setAttribute('data-disabled', 'true');
            this.toggle.disabled = true;
        } else {
            this.wrapper.setAttribute('data-disabled', 'false');
            this.toggle.disabled = false;
        }

        // Insert the custom select after the original
        this.element.parentNode.insertBefore(this.wrapper, this.element.nextSibling);
    }



    createToggle() {
        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'custom-select__toggle';
        this.toggle.setAttribute('aria-haspopup', 'listbox');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.setAttribute('aria-labelledby', this.element.id || '');
        
        // Create label span
        this.label = document.createElement('span');
        this.label.className = 'custom-select__label';
        
        // Create arrow
        this.arrow = document.createElement('span');
        this.arrow.className = 'custom-select__arrow';
        this.arrow.innerHTML = `
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.410704 1.12689C0.736141 0.801453 1.26378 0.801453 1.58921 1.12689L5.99996 5.53763L10.4107 1.12689C10.7361 0.801453 11.2638 0.801453 11.5892 1.12689C11.9147 1.45233 11.9147 1.97996 11.5892 2.3054L6.58922 7.3054C6.26378 7.63084 5.73614 7.63084 5.4107 7.3054L0.410704 2.3054C0.0852667 1.97996 0.0852667 1.45233 0.410704 1.12689Z" fill="currentColor"/>
            </svg>
        `;
        this.arrow.setAttribute('aria-hidden', 'true');

        this.toggle.appendChild(this.label);
        this.toggle.appendChild(this.arrow);
        this.wrapper.appendChild(this.toggle);
    }

    createDropdown() {
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'custom-select__dropdown';
        this.dropdown.setAttribute('role', 'listbox');
        this.dropdown.setAttribute('aria-labelledby', this.toggle.id || '');

        this.createDropdownOptions();
        this.wrapper.appendChild(this.dropdown);
    }

    createDropdownOptions() {
        this.selectOptions.forEach((option, index) => {
            const optionElement = document.createElement('div');
            optionElement.className = 'custom-select__option';
            optionElement.textContent = option.text;
            optionElement.setAttribute('role', 'option');
            optionElement.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false');
            optionElement.setAttribute('data-value', option.value);
            optionElement.setAttribute('data-index', index);
            
            if (index === this.selectedIndex) {
                optionElement.setAttribute('data-selected', 'true');
            }

            if (option.disabled) {
                optionElement.setAttribute('aria-disabled', 'true');
                optionElement.style.opacity = '0.5';
                optionElement.style.pointerEvents = 'none';
            }

            this.dropdown.appendChild(optionElement);
        });
    }

    bindEvents() {
        // Toggle click
        this.toggle.addEventListener('click', (e) => {
            e.preventDefault();
            if (!this.options.disabled) {
                this.toggleDropdown();
            }
        });

        // Keyboard navigation
        this.toggle.addEventListener('keydown', (e) => {
            this.handleKeydown(e);
        });

        // Option clicks
        this.dropdown.addEventListener('click', (e) => {
            const option = e.target.closest('.custom-select__option');
            if (option && !option.hasAttribute('aria-disabled')) {
                const index = parseInt(option.getAttribute('data-index'));
                this.selectOption(index);
                this.closeDropdown();
            }
        });

        // Option hover
        this.dropdown.addEventListener('mouseover', (e) => {
            const option = e.target.closest('.custom-select__option');
            if (option && !option.hasAttribute('aria-disabled')) {
                this.highlightOption(parseInt(option.getAttribute('data-index')));
            }
        });

        // Watch for all changes to the original select including innerHTML changes
        this.observer = new MutationObserver((mutations) => {
            let shouldRebuild = false;
            let shouldSync = false;
            
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    // Options were added/removed, need to rebuild
                    shouldRebuild = true;
                } else if (mutation.type === 'attributes') {
                    if (mutation.attributeName === 'value') {
                        shouldSync = true;
                    } else if (mutation.attributeName === 'disabled') {
                        // Disabled state changed, need to rebuild
                        shouldRebuild = true;
                    }
                }
            });
            
            if (shouldRebuild) {
                this.rebuildCustomSelect();
            } else if (shouldSync) {
                this.syncFromOriginalSelect();
            }
        });
        
        this.observer.observe(this.element, { 
            childList: true, 
            subtree: true, 
            attributes: true, 
            attributeFilter: ['value', 'disabled']
        });

        // Listen for change events on the original select (from jQuery .val() calls)
        this.element.addEventListener('change', () => {
            this.syncFromOriginalSelect();
        });

        // Listen for input events as well (covers more cases than just change)
        this.element.addEventListener('input', () => {
            this.syncFromOriginalSelect();
        });

        // Create a more robust observer for property changes
        this.originalValueGetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').get;
        this.originalValueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;
        this.originalDisabledGetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'disabled').get;
        this.originalDisabledSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'disabled').set;
        
        const self = this;
        Object.defineProperty(this.element, 'value', {
            get: function() {
                return self.originalValueGetter.call(this);
            },
            set: function(val) {
                self.originalValueSetter.call(this, val);
                self.syncFromOriginalSelect();
            }
        });

        Object.defineProperty(this.element, 'disabled', {
            get: function() {
                return self.originalDisabledGetter.call(this);
            },
            set: function(disabled) {
                self.originalDisabledSetter.call(this, disabled);
                self.syncDisabledState();
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.wrapper.contains(e.target)) {
                this.closeDropdown();
            }
        });

        // Close dropdown on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closeDropdown();
                this.toggle.focus();
            }
        });
    }

    handleKeydown(e) {
        if (this.options.disabled) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (this.isOpen) {
                    if (this.highlightedIndex >= 0) {
                        this.selectOption(this.highlightedIndex);
                    }
                    this.closeDropdown();
                } else {
                    this.openDropdown();
                }
                break;

            case 'ArrowDown':
                e.preventDefault();
                if (this.isOpen) {
                    this.highlightNextOption();
                } else {
                    this.openDropdown();
                }
                break;

            case 'ArrowUp':
                e.preventDefault();
                if (this.isOpen) {
                    this.highlightPreviousOption();
                } else {
                    this.openDropdown();
                }
                break;

            case 'Home':
                e.preventDefault();
                if (this.isOpen) {
                    this.highlightOption(0);
                }
                break;

            case 'End':
                e.preventDefault();
                if (this.isOpen) {
                    this.highlightOption(this.selectOptions.length - 1);
                }
                break;

            case 'Tab':
                if (this.isOpen) {
                    this.closeDropdown();
                }
                break;
        }
    }

    openDropdown() {
        if (this.options.disabled) return;

        this.isOpen = true;
        this.wrapper.setAttribute('data-open', 'true');
        this.toggle.setAttribute('aria-expanded', 'true');
        this.highlightedIndex = this.selectedIndex;
        this.updateHighlight();

        // Scroll highlighted option into view
        const highlightedOption = this.dropdown.children[this.highlightedIndex];
        if (highlightedOption) {
            highlightedOption.scrollIntoView({ block: 'nearest' });
        }
    }

    closeDropdown() {
        this.isOpen = false;
        this.wrapper.setAttribute('data-open', 'false');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.highlightedIndex = -1;
        this.updateHighlight();
    }

    toggleDropdown() {
        if (this.isOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    selectOption(index) {
        if (index < 0 || index >= this.selectOptions.length) return;
        if (this.selectOptions[index].disabled) return;

        const previousIndex = this.selectedIndex;
        this.selectedIndex = index;
        const selectedOption = this.selectOptions[index];

        // Update original select
        this.element.value = selectedOption.value;
        this.element.selectedIndex = index;



        // Update display
        this.updateSelectedDisplay();
        this.updateAriaAttributes();
        this.updateChangedState(); // Update changed state after selection

        // Trigger change event on original select
        const changeEvent = new Event('change', { bubbles: true });
        this.element.dispatchEvent(changeEvent);

        // Call onChange callback
        if (this.options.onChange && typeof this.options.onChange === 'function') {
            this.options.onChange(selectedOption.value, selectedOption.text, previousIndex);
        }
    }

    highlightOption(index) {
        if (index < 0 || index >= this.selectOptions.length) return;
        if (this.selectOptions[index].disabled) return;

        this.highlightedIndex = index;
        this.updateHighlight();
    }

    highlightNextOption() {
        let nextIndex = this.highlightedIndex + 1;
        while (nextIndex < this.selectOptions.length && this.selectOptions[nextIndex].disabled) {
            nextIndex++;
        }
        if (nextIndex < this.selectOptions.length) {
            this.highlightOption(nextIndex);
        }
    }

    highlightPreviousOption() {
        let prevIndex = this.highlightedIndex - 1;
        while (prevIndex >= 0 && this.selectOptions[prevIndex].disabled) {
            prevIndex--;
        }
        if (prevIndex >= 0) {
            this.highlightOption(prevIndex);
        }
    }

    updateHighlight() {
        Array.from(this.dropdown.children).forEach((option, index) => {
            option.setAttribute('data-highlighted', index === this.highlightedIndex ? 'true' : 'false');
        });
    }

    updateSelectedDisplay() {
        const selectedOption = this.selectOptions[this.selectedIndex];
        this.label.textContent = selectedOption ? selectedOption.text : this.options.placeholder;

        Array.from(this.dropdown.children).forEach((option, index) => {
            const isSelected = index === this.selectedIndex;
            option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            option.setAttribute('data-selected', isSelected ? 'true' : 'false');
        });

        this.updateHasValueState();
    }

    updateHasValueState() {
        const selectedOption = this.selectOptions[this.selectedIndex];
        const value = selectedOption ? selectedOption.value : null;
        const hasValue = value !== null && value !== undefined && String(value).length > 0;
        this.wrapper.setAttribute('data-has-value', hasValue ? 'true' : 'false');
    }

    updateAriaAttributes() {
        const selectedOption = this.selectOptions[this.selectedIndex];
        if (selectedOption) {
            this.toggle.setAttribute('aria-describedby', `Selected: ${selectedOption.text}`);
        }
    }

    // Check if current value differs from initial value
    hasValueChanged() {
        const currentValue = this.selectOptions[this.selectedIndex]?.value || null;
        // Use trueDefaultValue if provided, otherwise fall back to initialValue
        const baselineValue = this.options.trueDefaultValue !== null ? this.options.trueDefaultValue : this.initialValue;
        
        // Convert both values to strings for comparison to handle type mismatches
        // Handle null/undefined separately to avoid converting 0 to null
        const currentValueStr = currentValue !== null && currentValue !== undefined ? String(currentValue) : null;
        const baselineValueStr = baselineValue !== null && baselineValue !== undefined ? String(baselineValue) : null;
        
        return currentValueStr !== baselineValueStr;
    }

    // Update the visual state based on whether value has changed
    updateChangedState() {
        const hasChanged = this.hasValueChanged();
        this.wrapper.setAttribute('data-changed', hasChanged ? 'true' : 'false');
    }

    setInitialValue() {
        // Sync with the original select's current value first
        this.syncFromOriginalSelect();
        this.updateSelectedDisplay();
        this.updateAriaAttributes();
        this.updateChangedState(); // Set initial changed state
    }

    syncFromOriginalSelect() {
        const currentValue = this.element.value;
        const index = this.selectOptions.findIndex(opt => opt.value === currentValue);
        
        if (index >= 0) {
            this.selectedIndex = index;
            this.updateSelectedDisplay();
            this.updateAriaAttributes();
            this.updateChangedState(); // Sync changed state
        }
    }

    syncDisabledState() {
        // Update disabled state based on original select element
        const isDisabled = this.element.disabled || this.element.hasAttribute('disabled');
        this.options.disabled = isDisabled;
        
        // Update wrapper and toggle button
        if (isDisabled) {
            this.wrapper.setAttribute('data-disabled', 'true');
            this.toggle.disabled = true;
        } else {
            this.wrapper.setAttribute('data-disabled', 'false');
            this.toggle.disabled = false;
        }
    }

    rebuildCustomSelect() {
        // Store current value
        const currentValue = this.element.value;
        
        // Reparse the options (this will also check disabled state)
        this.parseOriginalSelect();
        
        // Update disabled state on wrapper
        if (this.options.disabled) {
            this.wrapper.setAttribute('data-disabled', 'true');
            this.toggle.disabled = true;
        } else {
            this.wrapper.setAttribute('data-disabled', 'false');
            this.toggle.disabled = false;
        }
        
        // Rebuild dropdown
        this.dropdown.innerHTML = '';
        this.createDropdownOptions();
        
        // Restore the selected value
        const index = this.selectOptions.findIndex(opt => opt.value === currentValue);
        if (index >= 0) {
            this.selectedIndex = index;
        } else if (this.selectOptions.length > 0) {
            this.selectedIndex = 0;
        }
        
        this.updateSelectedDisplay();
        this.updateAriaAttributes();
        this.updateChangedState(); // Rebuild changed state
    }

    // Public methods
    getValue() {
        return this.selectedIndex >= 0 ? this.selectOptions[this.selectedIndex].value : null;
    }

    setValue(value) {
        const index = this.selectOptions.findIndex(opt => opt.value === value);
        if (index >= 0) {
            this.selectOption(index);
        }
    }

    disable() {
        this.options.disabled = true;
        this.wrapper.setAttribute('data-disabled', 'true');
        this.toggle.disabled = true;
    }

    enable() {
        this.options.disabled = false;
        this.wrapper.setAttribute('data-disabled', 'false');
        this.toggle.disabled = false;
    }

    destroy() {
        // Disconnect observer
        if (this.observer) {
            this.observer.disconnect();
        }

        // Restore original property descriptors
        if (this.originalValueGetter && this.originalValueSetter) {
            Object.defineProperty(this.element, 'value', {
                get: this.originalValueGetter,
                set: this.originalValueSetter
            });
        }

        if (this.originalDisabledGetter && this.originalDisabledSetter) {
            Object.defineProperty(this.element, 'disabled', {
                get: this.originalDisabledGetter,
                set: this.originalDisabledSetter
            });
        }

        // Remove event listeners
        document.removeEventListener('click', this.closeDropdown);
        document.removeEventListener('keydown', this.handleKeydown);
        
        // Show original select
        this.element.style.display = '';
        
        // Remove initialization marker
        this.element.removeAttribute('data-custom-select-initialized');
        
        // Remove custom structure
        if (this.wrapper && this.wrapper.parentNode) {
            this.wrapper.parentNode.removeChild(this.wrapper);
        }

        // Unregister this instance (only if element has an ID)
        if (this.element.id) {
            CustomSelect.instances.delete(this.element.id);
        }
    }

    /**
     * Static method to sync a specific custom select by element ID
     * @param {string} elementId - The ID of the select element to sync
     * @returns {boolean} - True if sync was successful, false if instance not found
     */
    static syncById(elementId) {
        const instance = CustomSelect.instances.get(elementId);
        if (instance) {
            instance.syncFromOriginalSelect();
            return true;
        }
        return false;
    }

    /**
     * Static method to initialize all select elements
     * @param {string} selector - CSS selector for select elements to initialize
     * @param {object} options - Options to pass to each CustomSelect instance
     * @param {object} trueDefaults - Optional mapping of element IDs to their true default values
     * @returns {Array} - Array of created CustomSelect instances
     */
    static initializeAll(selector = 'select[data-custom-select]', options = {}, trueDefaults = {}) {
        const selects = document.querySelectorAll(selector);
        const instances = [];

        selects.forEach(select => {
            // Skip if already initialized
            if (!select.hasAttribute('data-custom-select-initialized')) {
                // Create options object with true default value if provided
                const instanceOptions = { ...options };
                if (select.id && trueDefaults[select.id] !== undefined) {
                    instanceOptions.trueDefaultValue = trueDefaults[select.id];
                }
                
                const instance = new CustomSelect(select, instanceOptions);
                instances.push(instance);
            }
        });

        return instances;
    }
}

// No auto-initialization - always require manual initialization by the caller

// Export for module usage
export default CustomSelect; 